import {
  AgendaSpeaker,
  PublicEventDetailsResponse,
  PublicExhibitionItem,
} from '@hubs101/booking-api-skd-client/lib/types';
import { Document, Image, Page, Text, View } from '@react-pdf/renderer';
import { Trans, useTranslation } from 'react-i18next';

import AttendeeList from './components/AttendeeList';
import ContactUs from './components/ContactUs';
import { DefaultTheme } from 'styled-components';
import IncreaseSales from './components/IncreaseSales';
import KeyTopics from './components/KeyTopics';
import MatchMaking from './components/MatchMaking';
import PackagesPrices from './components/PackagesPrices';
import ParticipantFeedback from './components/ParticipantFeedback';
import PartnerDetails from './components/PartnerDetails';
import ServiceThatConvince from './components/ServiceThatConvince';
import SpeakerPanel from './components/SpeakerPanel';
import TargetGroups from './components/TargetGroups';
import WhoWhy from './components/WhoWhy';
import { eventTimeFormat } from '@utils/time';
import { getImageSrc } from '@utils/fn';
import { styles as glanceStyles } from '@composites/PDF/AgendaPdfContent/components/EventAtGlance/styles';
import { globalStyles } from '@composites/PDF/AgendaPdfContent/styles';
import { styles } from './styles';
import { stylesAwesome } from '@composites/PDF/AgendaPdfContent/components/EventAtGlance';
import { useMemo } from 'react';
import weLogoWhite from '@assets/images/we_conect_logo_white.png';
import weLogoWhiteLove from '@assets/images/made_with_love_white.png';

interface PartnerProspectusPdfContentProps {
  event: PublicEventDetailsResponse;
  theme: DefaultTheme;
  language: 'en' | 'de';
  agendaSpeakers: AgendaSpeaker[];
  exhibitions: PublicExhibitionItem[];
}

const PartnerProspectusPdfContent = ({
  event,
  language,
  agendaSpeakers,
  exhibitions,
}: PartnerProspectusPdfContentProps) => {
  const { t } = useTranslation();

  const eventName = event?.name;

  const eventStatisticsItems = useMemo(
    () =>
      event?.website_content?.whoattend
        ?.filter(e => e)
        ?.sort((a, b) => a.order - b.order)
        ?.map(whoAttend => ({
          description: whoAttend?.description,
          title: whoAttend?.value,
          icon: whoAttend?.icon?.split('-')?.join('_'),
        })),
    [event?.website_content?.whoattend]
  );

  const header = useMemo(() => {
    return (
      <View style={styles.eventHeaderLogoWrapper} fixed>
        <Image
          style={styles.eventHeaderLogo}
          src={getImageSrc(event?.images.logoTransparent)}
        />
      </View>
    );
  }, [event?.images.logoTransparent]);

  const footer = useMemo(() => {
    return (
      <View
        style={{
          ...styles.footer,
          backgroundColor: event?.design?.themeColor ?? '#009FE3',
        }}
        fixed
      >
        <View>
          <Text style={styles.footerText}>{event?.name}</Text>
        </View>
        <View style={styles.logosWrapper}>
          <Image style={styles.logo} src={weLogoWhite} />
          <Image style={styles.logo} src={weLogoWhiteLove} />
        </View>
        <View>
          <Text style={styles.footerText}>
            {eventTimeFormat(
              event?.start_date ?? '',
              event?.end_date ?? '',
              language
            )}
          </Text>
        </View>
      </View>
    );
  }, [
    event?.design?.themeColor,
    event?.end_date,
    event?.name,
    event?.start_date,
    language,
  ]);

  return (
    <Document>
      <Page size="A4" orientation="landscape">
        {header}
        <View style={styles.bodyContainer}>
          <View style={styles.leftSideBody}>
            <Image
              style={styles.bigEventLogo}
              src={getImageSrc(event?.images.logoTransparent)}
            />
          </View>
          <View style={styles.rightSideBody}>
            <Text style={styles.title}>{t('business-partner-packages')}</Text>

            <Text style={styles.regularText}>{event?.description}</Text>
          </View>
        </View>
        {footer}
      </Page>
      <Page size="A4" orientation="landscape">
        <View style={styles.bodyContainer}>
          <View
            style={{
              ...styles.leftSideBody,
              backgroundColor: event.design.themeColor,
              flexDirection: 'column',
              gap: 14,
            }}
          >
            <Text style={styles.boldText}>
              {t('prospectus-content-quote-text')}
            </Text>
            <Text style={styles.quoteText}>
              {t('prospectus-content-quote-author')}
            </Text>
          </View>
          <View style={{ ...styles.rightSideBody, gap: 20 }}>
            <Text style={{ ...styles.title, paddingLeft: 15 }}>
              {t('become-a-partner-!')}
            </Text>
            <View
              style={{
                ...globalStyles.lineBar,
                marginRight: 'auto',
              }}
            />
            <Text
              style={{
                ...styles.regularText,
                textTransform: 'uppercase',
                paddingLeft: 15,
              }}
            >
              <Trans i18nKey="become-a-partner-text" eventName={eventName}>
                and get ready for the {{ eventName }}!
              </Trans>
            </Text>
          </View>
        </View>
        {footer}
      </Page>

      <Page size="A4" orientation="landscape">
        {header}
        <View>
          <View
            style={{
              ...styles.rightSideBody,
              gap: 20,
              alignItems: 'flex-start',
            }}
          >
            <Text style={styles.bodyTitle}>{t('prospectus-title-1')}</Text>
            <Text style={styles.bodyTitle}>{t('prospectus-title-2')}</Text>
            <View style={styles.bodyLineBar} />
          </View>
          <View style={styles.itemsWrapper}>
            {eventStatisticsItems?.map(item => (
              <View style={glanceStyles.eventStatisticsItem} key={item.title}>
                <Text style={{ ...stylesAwesome.icon, fontSize: 37 }}>
                  {'\uf6ff'}
                </Text>
                <View style={{ ...glanceStyles.textWrapper }}>
                  <Text style={{ ...glanceStyles.boldText, fontSize: 23 }}>
                    {item.title}
                  </Text>
                  <Text
                    style={{ ...glanceStyles.regularText, fontWeight: 400 }}
                  >
                    {item.description}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>
        {footer}
      </Page>

      <Page size="A4" orientation="landscape">
        {header}
        <TargetGroups event={event} />
        {footer}
      </Page>
      <Page size="A4" orientation="landscape">
        <SpeakerPanel event={event} agendaSpeakers={agendaSpeakers} />
        {footer}
      </Page>

      <Page size="A4" orientation="landscape">
        <WhoWhy event={event} />
        {footer}
      </Page>

      <Page size="A4" orientation="landscape">
        {header}
        <KeyTopics event={event} />
        {footer}
      </Page>
      <Page size="A4" orientation="landscape">
        {header}
        <IncreaseSales event={event} />
        {footer}
      </Page>
      <Page size="A4" orientation="landscape">
        {header}
        <MatchMaking event={event} />
        {footer}
      </Page>
      <Page size="A4" orientation="landscape">
        {header}
        <ServiceThatConvince event={event} />
        {footer}
      </Page>
      <Page size="A4" orientation="landscape">
        <ParticipantFeedback event={event} />
        {footer}
      </Page>
      <Page size="A4" orientation="landscape">
        <AttendeeList event={event} />
        {footer}
      </Page>
      <Page size="A4" orientation="landscape">
        <PackagesPrices event={event} />
        {footer}
      </Page>
      <Page size="A4" orientation="landscape">
        {header}
        <PartnerDetails event={event} />
        {footer}
      </Page>
      <Page size="A4" orientation="landscape">
        {header}
        <ContactUs event={event} />
        {footer}
      </Page>
    </Document>
  );
};

export default PartnerProspectusPdfContent;
